/**
 * Storage Document
 * Ported from: innovation-claude/packages/storage/src/document.rs
 *
 * Typed document bound to a key and StorageEngine.
 * Provides caching to skip redundant saves when value unchanged.
 */

import type { Storage } from './Storage'
import type { StorageEngine } from './StorageEngine'
import { StorageError } from './StorageError'

/**
 * A typed storage document bound to a specific key and engine
 *
 * Features:
 * - Type-safe with generics
 * - Caching (skips redundant saves)
 * - Delegates to StorageEngine
 *
 * Example:
 * ```typescript
 * const engine = StorageEngine.local()
 * const doc = engine.document<UserPrefs>('user-prefs')
 * await doc.save({ locale: 'en', theme: 'dark' })
 * const prefs = await doc.load()
 * ```
 *
 * Ported from document.rs:34-181
 */
export class StorageDocument<T> implements Storage<T> {
    private cachedValue: T | null = null

    constructor(
        private _key: string,
        private engine: StorageEngine
    ) {}

    /**
     * Save data to storage
     * Skips save if value unchanged (optimization)
     * Ported from document.rs:69-94
     */
    async save(data: T): Promise<void> {
        // Check if value has changed since last save/load
        if (this.cachedValue !== null) {
            // Deep equality check via JSON comparison
            const currentJson = JSON.stringify(this.cachedValue)
            const newJson = JSON.stringify(data)

            if (currentJson === newJson) {
                // Value unchanged - skip save
                return
            }
        }

        // Value changed or first save - serialize and save
        let json: string
        try {
            json = JSON.stringify(data)
        } catch (error) {
            throw StorageError.serialization(
                `Failed to serialize data for key '${this._key}'`,
                error instanceof Error ? error : undefined
            )
        }

        await this.engine.saveRaw(this._key, json)

        // Update cache with new value
        this.cachedValue = data
    }

    /**
     * Load data from storage
     * Updates cache on successful load
     * Ported from document.rs:96-112
     */
    async load(): Promise<T | null> {
        const json = await this.engine.loadRaw(this._key)

        if (!json) {
            return null
        }

        // Deserialize
        let data: T
        try {
            data = JSON.parse(json) as T
        } catch (error) {
            throw StorageError.deserialization(
                `Failed to deserialize data for key '${this._key}'`,
                error instanceof Error ? error : undefined
            )
        }

        // Update cache
        this.cachedValue = data

        return data
    }

    /**
     * Delete data from storage
     * Clears cache
     * Ported from document.rs:114-116
     */
    async delete(): Promise<void> {
        await this.engine.deleteRaw(this._key)
        this.cachedValue = null
    }

    /**
     * Get the storage key
     * Ported from document.rs:118-120
     */
    key(): string {
        return this._key
    }
}
